import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import useShouldShowApplePay from '../../hooks/useShouldShowApplePay';
import AppPaths from '../../AppPaths';
import { useCartPricing } from '../../contexts/CartPricingContext';
import useCheckoutPaths from '../../hooks/useCheckoutPaths';
import usePayPalBilling from '../../hooks/usePayPalBilling';
import * as events from '../../lib/analytics/events';
import { useCartMode } from '../../lib/cartModes';
import CheckoutContext from '../../lib/CheckoutContext';
import { getDomainConfig } from '../../lib/domainConfig';
import { centsToDollars, centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import * as types from '../../types';
import LoginButton from '../../views/Checkout/components/LoginButton';
import ApplePayCheckout from '../ApplePayCheckout';
import Button from '../Button';
import ErrorMessage, { useError } from '../ErrorMessage';
import { SimpleDismissableModal } from '../modals';
import PayPalButton from '../PayPalButton';
import styles from './CartSummary.module.scss';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import {
  couponWaivesActivationFee,
  inCartPrepaidMembershipCount,
  inCartS3CollarCount,
} from '../ItemsInCart/CouponLineItemV2';
import classNames from 'classnames';
import { getFiGrowthBook } from '../../lib/growthbook';
import { householdDiscountReason } from '../MultidogDiscount/MultidogDiscountExistingCollars';
import { ReactComponent as IconShipping } from '../../assets/images/icons/icon_shipping.svg';
import { ReactComponent as IconReturns } from '../../assets/images/icons/returns.svg';
import { useLocale, FiCountry } from '../../contexts/LocaleContext';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { isBundleCartItem } from '../../lib/cart';
import { calculatePositionSavings } from '../../lib/positionDiscount';
import useFreeShippingDisplay from '../../hooks/useFreeShippingDisplay';
import useCartHasFreeTrialBundle from '../../hooks/useCartHasFreeTrialBundle';
import useCartHasOnlyFreeTrialBundles from '../../hooks/useCartHasOnlyFreeTrialBundles';
import useCartItemV2Enabled from '../../hooks/useCartItemV2Enabled';

type OnCheckoutFn = (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
interface CartActionProps {
  onCheckOut: OnCheckoutFn;
  showDigitalWallets: boolean;
  allowLogin?: boolean;
}

interface PaymentOptionsProps {
  onCheckOut: OnCheckoutFn;
  showDigitalWallets: boolean;
  digitalWalletsOnly: boolean;
}

export function getCartV2FormattedTotal(cents: number): string {
  const hasNonZeroOnesOrTensDigit = (num: number): boolean => num % 10 !== 0 || Math.floor(num / 10) % 10 !== 0;
  if (hasNonZeroOnesOrTensDigit(cents)) {
    return centsToDollars(cents);
  }
  return centsToDollarsNumber(cents).toFixed(0);
}

/** Pick the appropriate currency formatter based on whether the value is a round dollar amount */
export function getCartV2CurrencyFormattedTotal(
  cents: number,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
): string {
  const dollarValue = centsToDollarsNumber(cents);
  if (dollarValue % 1 === 0) {
    return wholeNumberCurrencyFormatter.format(dollarValue);
  }
  return currencyFormatter.format(dollarValue);
}

export function getCartTestTotalDiscount(cartPricing: types.CartPricing, discounts: 'total' | 'promo_only'): number {
  const { cartItemCredits } = cartPricing;
  let activationFeeDiscount = discounts === 'promo_only' ? 0 : cartPricing.waivedActivationFeesInCents || 0;

  // We want to exclude free trial discounts from this since we don't want to include it as part of a "$ OFF" tag
  // cartItemCredits will only be populated if a discount was applied the physical product SKU or subscription,
  // so this will never include discounts on activation fees.
  const creditDiscount = Object.values(cartItemCredits || []).reduce(
    (acc, credits) =>
      acc +
      credits.reduce((accCredit, credit) => {
        if (credit.creditReason === 'free_trial') {
          return accCredit;
        }

        // We currently waive activation fees through a couple of different methods:
        //  a coupon code and multi-dog discounts.
        // cartPricing.waivedActivationFeesInCents includes both cases, so if we see that a promo code waived
        // the activation fee, we need to subtract it from the total waived activation fees so they don't get
        // counted twice.
        if (discounts === 'total' && credit.creditReason === 'promo_waived_activation_fee') {
          activationFeeDiscount -= credit.creditAmountInCents;
        }

        // If promo only, exclude multi-dog discounts
        if (
          discounts === 'promo_only' &&
          credit.creditReason !== 'promo' &&
          credit.creditReason !== 'promo_waived_activation_fee'
        ) {
          return accCredit;
        }

        return accCredit + credit.creditAmountInCents;
      }, 0),
    0,
  );

  // The above logic would result in total discount of $0 if the promo exclusively waived the activation fee,
  // but we want to display the activation fee waiver amount in those situations.
  // There is probably a more elegant way to adjust the logic, but this should suffice.
  if (creditDiscount === 0 && cartPricing.waivedActivationFeesInCents !== undefined) {
    activationFeeDiscount = cartPricing.waivedActivationFeesInCents;
  }

  const cartTestTotalDiscount =
    (cartPricing.appliedAccountBalanceInCents || 0) +
    (cartPricing.appliedGiftCardAmountInCents || 0) +
    creditDiscount +
    activationFeeDiscount;

  return cartTestTotalDiscount;
}

function TotalPrice() {
  const { t } = useTranslation();
  const cartPricing = useCartPricing();
  const { totalInCents, cartItemCredits } = cartPricing;
  const { cart } = useContext(CheckoutContext);
  const cartV2Enabled = showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-1', false);
  const { currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();

  // calculate position discount savings for the yellow pill
  const positionDiscountSavings = React.useMemo(() => {
    const { positionDiscounts } = cartPricing;
    if (!positionDiscounts) return 0;

    return Object.entries(positionDiscounts).reduce((total, [cartItemId, discount]) => {
      if (!discount.isEnrolled || discount.discountPercent === 0) return total;

      const cartItem = cart.cartItems[cartItemId];
      if (!cartItem || !isBundleCartItem(cartItem)) return total;

      const subscriptionProduct = productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product;
      if (!subscriptionProduct || !types.isModuleSubscriptionProduct(subscriptionProduct)) return total;

      const subscriptionPrice = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
      return total + calculatePositionSavings(subscriptionPrice, discount.discountPercent);
    }, 0);
  }, [cartPricing, cart.cartItems, productSkuMap, currency]);

  if (cartV2Enabled) {
    const creditDiscount = Object.values(cartItemCredits || []).reduce(
      (acc, credits) =>
        acc +
        credits.reduce((accCredit, credit) => {
          // Exclude cart item credits that we know are from the waived activation fee
          // because we'll look at cartPricing.waivedActivationFeesInCents separately.
          if (credit.creditReason === 'promo_waived_activation_fee') {
            return accCredit;
          }

          return accCredit + credit.creditAmountInCents;
        }, 0),
      0,
    );
    const couponApplied = cart.couponCode !== undefined;
    const referralCodeApplied = cart.referralCode !== undefined && !householdDiscountReason(cartPricing);

    // Experiment: 'nano99' is a coupon code that applies a discount directly to the pre-paid amount
    // While keeping the activation fee. Force the discount amount to display in the rendered component.
    const elevatedNanoCodeApplied = cart.couponCode === 'nano99';
    const cartItems = Object.values(cart.cartItems);
    const totalCollarCount = inCartS3CollarCount(cartItems);
    const monthToMonthCollarCount = inCartPrepaidMembershipCount(cartItems);
    const activationFeeDiscount = cartPricing.waivedActivationFeesInCents || 0;
    const showActivationFeeDiscount =
      ((couponApplied && couponWaivesActivationFee(cartPricing, cart)) ||
        referralCodeApplied ||
        elevatedNanoCodeApplied) &&
      activationFeeDiscount > 0 &&
      totalCollarCount > 0 &&
      totalCollarCount - monthToMonthCollarCount > 0;
    // The elevated nano code applies a discount to the pre-paid amount, but not the activation fee.
    const totalDiscount = elevatedNanoCodeApplied ? creditDiscount : activationFeeDiscount + creditDiscount;
    const originalTotalInCents = totalDiscount + positionDiscountSavings + totalInCents;

    const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
    // for cart test, exclude activation fee waivers - just show coupon + gift card/account balance
    // position discount is handled separately since it's shown in its own pill
    const cartTestCreditDiscount = Object.values(cartItemCredits || []).reduce(
      (acc, credits) =>
        acc +
        credits.reduce((accCredit, credit) => {
          // exclude activation fee waivers and free trials
          if (credit.creditReason === 'promo_waived_activation_fee' || credit.creditReason === 'free_trial') {
            return accCredit;
          }
          return accCredit + credit.creditAmountInCents;
        }, 0),
      0,
    );
    // Don't include position discount in cartTestTotalDiscount - it's shown separately in the pill
    // and subtracted from the final total display
    const cartTestTotalDiscount =
      (cartPricing.appliedAccountBalanceInCents || 0) +
      (cartPricing.appliedGiftCardAmountInCents || 0) +
      cartTestCreditDiscount;

    // Calculate the total discount amount for the badge (coupons + position discount + waived activation fees)
    const waivedActivationFees = cartPricing.waivedActivationFeesInCents || 0;
    const totalSavingsForBadge = cartTestTotalDiscount + positionDiscountSavings + waivedActivationFees;
    // For strikethrough: show original price before all discounts when there are other discounts,
    // or just totalInCents when only position discount applies
    const hasOtherDiscounts = cartTestTotalDiscount > 0 || waivedActivationFees > 0;
    const hasPositionDiscountSavings = positionDiscountSavings > 0;
    const showStrikethrough = hasOtherDiscounts || hasPositionDiscountSavings;
    // Exclude tax from total since it's displayed separately in TaxPrice
    const taxInCents = cartPricing.taxInCents || 0;
    const totalWithoutTax = totalInCents - taxInCents;
    const strikethroughValue = hasOtherDiscounts
      ? totalWithoutTax + cartTestTotalDiscount + waivedActivationFees
      : totalWithoutTax;
    // Final displayed price subtracts position discount to show effective price
    const displayedFinalPrice = totalWithoutTax - positionDiscountSavings;

    return (
      <div className={classNames(styles.nanoTotalPrice, styles.priceData)}>
        <div>{t('subtotal')}</div>
        <div className={styles.pricingContainer}>
          {!showCartTest && showActivationFeeDiscount && (
            <div className={styles.originalPriceValue}>
              {getCartV2CurrencyFormattedTotal(originalTotalInCents, currencyFormatter, wholeNumberCurrencyFormatter)}
            </div>
          )}
          {showCartTest && showStrikethrough && (
            <div className={styles.originalPriceValue}>
              {getCartV2CurrencyFormattedTotal(strikethroughValue, currencyFormatter, wholeNumberCurrencyFormatter)}
            </div>
          )}
          <div className={styles.priceValue}>
            {(() => {
              const base = showCartTest ? displayedFinalPrice : totalInCents;
              const amount = base;
              return getCartV2CurrencyFormattedTotal(amount, currencyFormatter, wholeNumberCurrencyFormatter);
            })()}
          </div>
          {!showCartTest && showActivationFeeDiscount && (
            <div className={styles.dollarsOffValue}>
              {t('dollars_off', {
                amount: getCartV2CurrencyFormattedTotal(totalDiscount, currencyFormatter, wholeNumberCurrencyFormatter),
              })}
            </div>
          )}
          {showCartTest && totalSavingsForBadge > 0 && cartPricing.couponDiscount?.type !== 'free_trial' && (
            <div className={styles.dollarsOffValue}>
              {t('dollars_off', {
                amount: getCartV2CurrencyFormattedTotal(
                  totalSavingsForBadge,
                  currencyFormatter,
                  wholeNumberCurrencyFormatter,
                ),
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceData}>
      <div>{t('subtotal')}</div>
      <div className={styles.priceValue}>{currencyFormatter.format(centsToDollarsNumber(totalInCents))}</div>
    </div>
  );
}

function TaxPrice() {
  const { t } = useTranslation();
  const { currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const cartPricing = useCartPricing();
  const cartItemV2Enabled = useCartItemV2Enabled();
  const { taxInCents } = cartPricing;
  if (!taxInCents || cartItemV2Enabled) {
    return null;
  }

  return (
    <div className={classNames(styles.taxPrice, styles.priceData)}>
      <div>{t('tax')}</div>
      <div className={styles.pricingContainer}>
        <div className={styles.priceValue}>
          {getCartV2CurrencyFormattedTotal(taxInCents, currencyFormatter, wholeNumberCurrencyFormatter)}
        </div>
      </div>
    </div>
  );
}

export function PaymentOptions({ onCheckOut, showDigitalWallets, digitalWalletsOnly }: PaymentOptionsProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const showApplePay = useShouldShowApplePay() && !getFiGrowthBook().getFeatureValue('express-checkout-wallets', false);
  const cartPricing = useCartPricing();
  const { cart } = useContext(CheckoutContext);
  const checkoutPaths = useCheckoutPaths();
  const { applePayErrorAlertWhenAccountExists, showPayPal } = useCartMode();
  const { clearError, error, errorID, setError } = useError();

  const onStartPayPal = useCallback(clearError, [clearError]);
  const { onComplete: onCompletePayPal } = usePayPalBilling({ onError: setError });

  const onError = useCallback(
    (err: Error) => {
      setError(err.message);
    },
    [setError],
  );

  return (
    <div className={styles.cartPayment}>
      {!digitalWalletsOnly && <TaxPrice />}
      {!digitalWalletsOnly && <TotalPrice />}
      <div className={styles.summaryActions}>
        {!digitalWalletsOnly && (
          <Button
            className={styles.checkoutButton}
            onClick={(e) => {
              onCheckOut(cart, cartPricing);
              const useUnifiedCheckout = getFiGrowthBook().getFeatureValue('unified-checkout', false);
              const useStripeCheckout = getFiGrowthBook().getFeatureValue('stripe-elements-checkout', false);
              if (useUnifiedCheckout) {
                history.push(checkoutPaths.UnifiedCheckout);
              } else if (useStripeCheckout) {
                history.push(checkoutPaths.StripeCheckout);
              } else {
                history.push(checkoutPaths.Shipping);
              }
            }}
          >
            {!showDigitalWallets ? t('checkout') : t('pay_with_card')}
          </Button>
        )}
        {showApplePay && showDigitalWallets && (
          <ApplePayCheckout
            errorAlertOnAccountExists={applePayErrorAlertWhenAccountExists}
            events={events.cartPage}
            onError={onError}
          />
        )}
        {showPayPal && showDigitalWallets && (
          <PayPalButton
            events={events.cartPage}
            onComplete={onCompletePayPal}
            onError={onError}
            onStart={onStartPayPal}
          />
        )}
      </div>
      {error && (
        <div className={styles.summaryError}>
          <ErrorMessage errors={[error]} errorID={errorID} />
        </div>
      )}
    </div>
  );
}

function FreeShipping() {
  const { t } = useTranslation();
  const { country } = useLocale();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();
  const hasFreeTrialBundle = useCartHasFreeTrialBundle();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const isCanada = country === FiCountry.CA;
  const isCanadaFreeTrial = isCanada && hasFreeTrialBundle;

  const shippingText = onlyFreeTrials
    ? t('cancel_anytime')
    : isFreeShipping
      ? t('free_ground_shipping')
      : t('ground_shipping_price', { price: shippingPrice });

  // canada free trial users get different badges
  if (isCanadaFreeTrial) {
    return (
      <div className={styles.freeShippingContainer}>
        <div className={styles.freeShipping}>
          <IconReturns />
          {t('trust_badges.risk_free')}
        </div>
        <div className={styles.localShipping}>
          <span className={styles.localShippingIcon}>{'🇨🇦'}</span>
          {t('trust_badges.now_available_canada')}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.freeShippingContainer, { [styles.threeItems]: isCanada })}>
      <div className={styles.freeShipping}>
        <IconShipping />
        {shippingText}
      </div>
      <div className={styles.riskFree}>
        <IconReturns />
        {hasFreeTrialBundle ? t('free_for_days', { days: 30 }) : t('thirty_days_risk_free')}
      </div>
      {isCanada && (
        <div className={styles.localShipping}>
          <span className={styles.localShippingIcon}>{'🇨🇦'}</span>
          {t('canada_local_shipping')}
        </div>
      )}
    </div>
  );
}

function TermsOfServiceAndPrivacyPolicy() {
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();
  return (
    <div className={styles.termsOfServiceAndPrivacyPolicy}>
      <Trans
        i18nKey="terms_of_service_and_privacy_policy"
        values={{
          terms: t('terms_of_service'),
          privacy: t('privacy_policy'),
        }}
        components={{
          terms: (
            <span
              className={styles.termsOfService}
              onClick={() => {
                window.open(`https://${baseDomain}/tos`, '_blank');
              }}
            />
          ),
          privacy: (
            <span
              className={styles.privacyPolicy}
              onClick={() => {
                window.open(`https://${baseDomain}/privacypolicy`, '_blank');
              }}
            />
          ),
        }}
      />
    </div>
  );
}

export default function CartActions({ onCheckOut, showDigitalWallets, allowLogin }: CartActionProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { cart, session } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const checkoutPaths = useCheckoutPaths();
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const expressCheckoutEnabled = getFiGrowthBook().getFeatureValue('express-checkout-wallets', false);
  const hasFreeTrialBundle = useCartHasFreeTrialBundle();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();

  return (
    <>
      <div className={styles.desktopCartActions}>
        {showEcomCartTest && <FreeShipping />}
        {showEcomCartTest && (
          <div className={styles.desktopLoginButton}>
            {!session && allowLogin && (
              <div className={styles.loginButton}>
                <LoginButton returnTo={AppPaths.Bag} />
              </div>
            )}
          </div>
        )}
        <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={true} digitalWalletsOnly={false} />
        {hasFreeTrialBundle && onlyFreeTrials && (
          <div className={styles.freeTrialFooter}>
            {t('cart_item_v2.free_trial_footer', { duration: t('subscription_day', { days: 30 }) })}
          </div>
        )}
        {showEcomCartTest && (
          <div className={styles.desktopTermsOfServiceAndPrivacyPolicy}>
            <TermsOfServiceAndPrivacyPolicy />
          </div>
        )}
      </div>
      <div className={styles.mobileFreeShipping}>{showEcomCartTest && <FreeShipping />}</div>
      <div className={styles.footer}>
        {!session && allowLogin && (
          <>
            <div className={styles.loginButton}>
              <LoginButton returnTo={AppPaths.Bag} />
            </div>
            <TermsOfServiceAndPrivacyPolicy />
          </>
        )}
      </div>
      <div className={styles.mobileActionSheet}>
        {showDigitalWallets && expressCheckoutEnabled && (
          <>
            <TaxPrice />
            <TotalPrice />
            <Button
              type="button"
              onClick={() => {
                onCheckOut(cart, cartPricing);
                history.push(checkoutPaths.StripeCheckout);
              }}
            >
              {t('checkout')}
            </Button>
          </>
        )}
        {showDigitalWallets && !expressCheckoutEnabled && (
          <>
            <TaxPrice />
            <TotalPrice />
            <SimpleDismissableModal trigger={<Button type="button">{t('checkout')}</Button>}>
              <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={true} digitalWalletsOnly={false} />
            </SimpleDismissableModal>
          </>
        )}
        {!showDigitalWallets && (
          <PaymentOptions onCheckOut={onCheckOut} showDigitalWallets={false} digitalWalletsOnly={false} />
        )}
        {hasFreeTrialBundle && onlyFreeTrials && (
          <div className={styles.freeTrialFooter}>
            {t('cart_item_v2.free_trial_footer', { duration: t('subscription_day', { days: 30 }) })}
          </div>
        )}
      </div>
    </>
  );
}
